/**
 * Pure ordering helpers for DraggableCardGrid, kept out of the component so the
 * band-aware persistence logic is unit-testable without a DOM.
 *
 * A card belongs to a `section` (band). Cards only ever reorder WITHIN their
 * band; the effective render order is, per band in first-appearance order, the
 * saved order (present cards of that band only), with each card the saved order
 * has never seen placed right after the card it follows by default.
 * The flattened result is contiguous per band (band 0 keys, then band 1, ...),
 * which is what lets the drag handler move a card within its band by splicing
 * against its same-band neighbours in the flat array.
 */

export type CardMeta = {
  key: string
  section: number
}

export type BandedOrder = {
  /** Flat key order across all bands, contiguous per band, in render order. */
  flat: string[]
  /** Per-band ordered key lists, keyed by section index. */
  bands: Map<number, string[]>
}

export function computeBandedOrder(cards: CardMeta[], saved: string[]): BandedOrder {
  const sectionsInOrder: number[] = []
  cards.forEach((c) => {
    if (!sectionsInOrder.includes(c.section)) sectionsInOrder.push(c.section)
  })
  const bands = new Map<number, string[]>()
  const flat: string[] = []
  sectionsInOrder.forEach((s) => {
    const bandKeys = cards.filter((c) => c.section === s).map((c) => c.key)
    const bandSet = new Set(bandKeys)
    const ordered = saved.filter((k) => bandSet.has(k))
    bandKeys.forEach((key, i) => {
      if (ordered.includes(key)) return
      // A new card keeps its default place: after its natural predecessor, or
      // first when nothing precedes it.
      const before = bandKeys
        .slice(0, i)
        .reverse()
        .find((k) => ordered.includes(k))
      ordered.splice(before ? ordered.indexOf(before) + 1 : 0, 0, key)
    })
    bands.set(s, ordered)
    flat.push(...ordered)
  })
  return { flat, bands }
}

/**
 * SUPAGARD-26: fold a drag's result back into the full saved order.
 *
 * A drag only ever sees the cards on screen, but the saved order is shared by
 * every record of the page, and records show different cards (a registration
 * with no dealer has no Dealer card; a hidden card is off the grid). Saving the
 * drag's list as-is dropped every card this record did not show, so the next
 * record that DID show it fell back to its default place, and the user's
 * arrangement looked like it had not stuck.
 *
 * `next` (the on-screen order) wins for the cards it holds. Every card only the
 * previous saved order knows is kept, right after the card it followed there.
 */
export function mergeSavedOrder(previous: string[] | null, next: string[]): string[] {
  const merged = next.slice()
  if (!previous) return merged
  const present = new Set(next)
  // Walking the saved order, the card just handled is always the nearest one
  // before the current card that `merged` already holds, so an unseen card goes
  // straight after it (or first, when nothing precedes it).
  let insertAt = 0
  previous.forEach((key) => {
    if (present.has(key)) {
      insertAt = merged.indexOf(key) + 1
    } else {
      merged.splice(insertAt, 0, key)
      insertAt += 1
    }
  })
  return merged
}

/**
 * WPS-146: seat the cards of a two-column grid in rows so no row has a hole.
 *
 * Rows of two stretch to the taller card, so a short card (folded to its
 * header) beside a full one left a box-sized hole under it, and a page
 * rearranged a few times read as scattered. Each row takes the next card and,
 * when the card after it is of the same kind, that one too; otherwise the card
 * has the row to itself, which the grid draws full width (a folded card becomes
 * a slim bar). A wide card (a table) always has a row to itself. Cards are never taken out of the user's order, so a card always
 * sits exactly where it was dropped. Display only - the saved order is
 * untouched.
 */
export function seatCardsInRows(
  keys: string[],
  isShort: (key: string) => boolean,
  isWide: (key: string) => boolean = () => false,
): string[][] {
  const rows: string[][] = []
  for (let i = 0; i < keys.length; i++) {
    const next = keys[i + 1]
    // A wide card (a table) always has its row to itself.
    if (
      next !== undefined &&
      !isWide(keys[i]) &&
      !isWide(next) &&
      isShort(next) === isShort(keys[i])
    ) {
      rows.push([keys[i], next])
      i++
    } else {
      rows.push([keys[i]])
    }
  }
  return rows
}
